import { __ } from "@wordpress/i18n"
import { 
  useBlockProps, 
  InspectorControls,
  InnerBlocks,
  useInnerBlocksProps,
  RichText,
  MediaUpload,
  MediaUploadCheck
} from "@wordpress/block-editor"
import { 
  PanelBody,
  Button,
  SelectControl,
  TextControl
} from "@wordpress/components"

export default function Edit({ attributes, setAttributes, clientId }) {
  const blockProps = useBlockProps({
    className: 'big-card-block'
  })

  // Configure InnerBlocks like the tabs block does
  const innerBlocksProps = useInnerBlocksProps(
    {},
    {
      allowedBlocks: [
        'core/paragraph',
        'core/heading',
        'core/list',
        'core/columns',
        'core/column',
        'core/image',
        'core/video',
        'core/embed',
        'core/spacer',
        'core/quote',
        'core/separator',
        'puroclean/card',
        'puroclean/icon-and-title'
      ],
      template: [
        ['core/paragraph', { placeholder: 'Add your content here...' }]
      ],
      templateLock: false
    }
  )

  return (
    <>
      <InspectorControls>
        <PanelBody title={__('Big Card Settings', 'puroclean-tw')} initialOpen={true}>
          {/* Color Scheme */}
          <SelectControl
            label={__('Color Scheme', 'puroclean-tw')}
            value={attributes.colorScheme}
            options={[
              { label: 'Red', value: 'red' },
              { label: 'Blue', value: 'blue' }
            ]}
            onChange={(value) => setAttributes({ colorScheme: value })}
            style={{ marginBottom: '20px' }}
          />

          {/* Title */}
          <TextControl
            label={__('Title', 'puroclean-tw')}
            value={attributes.title}
            onChange={(value) => setAttributes({ title: value })}
            placeholder={__('Enter card title...', 'puroclean-tw')}
            style={{ marginBottom: '20px' }}
          />

          {/* Subtitle */}
          <TextControl
            label={__('Subtitle', 'puroclean-tw')}
            value={attributes.subtitle}
            onChange={(value) => setAttributes({ subtitle: value })}
            placeholder={__('Enter card subtitle...', 'puroclean-tw')}
            style={{ marginBottom: '20px' }}
          />

          {/* Right Link Text */}
          <TextControl
            label={__('Right Link Text', 'puroclean-tw')}
            value={attributes.rightLinkText}
            onChange={(value) => setAttributes({ rightLinkText: value })}
            placeholder={__('e.g., Contact Your Local PuroClean', 'puroclean-tw')}
            style={{ marginBottom: '20px' }}
          />

          {/* Right Link URL */}
          <TextControl
            label={__('Right Link URL', 'puroclean-tw')}
            value={attributes.rightLinkUrl}
            onChange={(value) => setAttributes({ rightLinkUrl: value })}
            placeholder={__('e.g., /contact or https://example.com', 'puroclean-tw')}
            style={{ marginBottom: '20px' }}
          />

          {/* Footer CTA Text */}
          <TextControl
            label={__('Footer CTA Title', 'puroclean-tw')}
            value={attributes.footerCtaTitle}
            onChange={(value) => setAttributes({ footerCtaTitle: value })}
            placeholder={__('e.g., Find Your Local PuroClean', 'puroclean-tw')}
            style={{ marginBottom: '20px' }}
          />

          {/* Footer CTA Subtitle OPTIONAL */}
          
          <TextControl
            label={__('Footer CTA Subtitle', 'puroclean-tw')}
            value={attributes.footerCtaSubtitle}
            onChange={(value) => setAttributes({ footerCtaSubtitle: value })}
            placeholder={__('e.g., Optional subtitle text', 'puroclean-tw')}
            style={{ marginBottom: '20px' }}
          />

          {/* Footer CTA Link */}
          <TextControl
            label={__('Footer CTA Link URL', 'puroclean-tw')}
            value={attributes.footerCtaLink}
            onChange={(value) => setAttributes({ footerCtaLink: value })}
            placeholder={__('e.g., /contact or https://example.com', 'puroclean-tw')}
            style={{ marginBottom: '20px' }}
          />

          {/* Footer CTA Background Image */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
              {__('Footer CTA Background Image:', 'puroclean-tw')}
            </label>
            <MediaUploadCheck>
              <MediaUpload
                onSelect={(media) => setAttributes({ 
                  footerCtaImage: {
                    url: media.url,
                    id: media.id,
                    alt: media.alt || 'CTA Background Image'
                  }
                })}
                allowedTypes={['image']}
                value={attributes.footerCtaImage?.id}
                render={({ open }) => (
                  <div>
                    {attributes.footerCtaImage?.url ? (
                      <div>
                        <img 
                          src={attributes.footerCtaImage.url} 
                          alt={attributes.footerCtaImage.alt}
                          style={{ maxWidth: '200px', marginBottom: '10px', display: 'block' }}
                        />
                        <Button onClick={open} variant="secondary" style={{ marginRight: '10px' }}>
                          {__('Change Image', 'puroclean-tw')}
                        </Button>
                        <Button 
                          onClick={() => setAttributes({ 
                            footerCtaImage: { url: '', id: 0, alt: 'CTA Background Image' }
                          })} 
                          variant="link" 
                          isDestructive
                        >
                          {__('Remove Image', 'puroclean-tw')}
                        </Button>
                      </div>
                    ) : (
                      <Button onClick={open} variant="secondary">
                        {__('Select CTA Background Image', 'puroclean-tw')}
                      </Button>
                    )}
                  </div>
                )}
              />
            </MediaUploadCheck>
          </div>
        </PanelBody>

        <PanelBody title={__('Advanced', 'puroclean-tw')} initialOpen={false}>
          {/* HTML Anchor */}
          <TextControl
            label={__('HTML Anchor', 'puroclean-tw')}
            value={attributes.anchor}
            onChange={(value) => setAttributes({ anchor: value })}
            placeholder={__('Enter anchor without #', 'puroclean-tw')}
            help={__('Anchors must be unique on the page and contain only lowercase letters, numbers, and hyphens.', 'puroclean-tw')}
            style={{ marginBottom: '20px' }}
          />
        </PanelBody>
      </InspectorControls>

      <div {...blockProps}>
        <div className="big-card-editor">
          {/* Actual preview that matches render.php */}
          <div style={{
            background: '#FFFFFF',
            boxShadow: '0px 0px 20px rgba(0,0,0,0.06)',
            borderRadius: '10px',
            overflow: 'hidden',
            margin: '20px 0'
          }}>
            {/* Top Banner Section - matches render.php */}
            <div style={{
              background: attributes.colorScheme === 'red' ? 'var(--wp--preset--color--primary, #dc2626)' : 'var(--wp--preset--color--secondary, #2563eb)',
              color: 'white',
              padding: '24px 16px'
            }}>
              <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '16px',
                  minHeight: (attributes.subtitle && attributes.subtitle.trim() !== '') ? 'auto' : '60px'
                }}>
                  {/* Title and Subtitle (Left side) */}
                  <div style={{ 
                    flex: '1',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: (attributes.subtitle && attributes.subtitle.trim() !== '') ? 'flex-start' : 'center'
                  }}>
                    <h2 style={{ 
                      color: 'white', 
                      margin: (attributes.subtitle && attributes.subtitle.trim() !== '') ? '0 0 8px 0' : '0', 
                      fontSize: '24px',
                      fontWeight: 'bold',
                      lineHeight: '1.2'
                    }}>
                      {attributes.title || 'Card Title'}
                    </h2>
                    {attributes.subtitle && attributes.subtitle.trim() !== '' && (
                      <p style={{ 
                        color: 'rgba(255, 255, 255, 0.9)', 
                        margin: '0',
                        fontSize: '16px',
                        lineHeight: '1.5'
                      }}>
                        {attributes.subtitle}
                      </p>
                    )}
                  </div>
                  
                  {/* Right Link (Right side) - arrow_link shortcode preview */}
                  {attributes.rightLinkText && attributes.rightLinkText.trim() !== '' && attributes.rightLinkUrl && attributes.rightLinkUrl.trim() !== '' && (
                    <div style={{ flexShrink: '0' }}>
                      <div style={{ 
                        display: 'inline-flex', 
                        alignItems: 'start',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: '600',
                        textDecoration: 'underline',
                        padding: '12px 0',
                        transition: 'all 0.5s',
                        cursor: 'pointer'
                      }}>
                        {/* SVG Arrow Icon - simulating puroclean_icon('linking-arrow') */}
                        <svg 
                          style={{ 
                            width: '20px', 
                            height: '20px', 
                            marginRight: '8px',
                            flexShrink: '0',
                            transition: 'transform 0.5s'
                          }} 
                          fill="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path d="M16.01 11H4v2h12.01v3L20 12l-3.99-4z"/>
                        </svg>
                        <span style={{ lineHeight: '1.2' }}>
                          {attributes.rightLinkText}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Content Area - matches render.php */}
            <div style={{ padding: '24px 16px' }}>
              <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
                <div style={{ 
                  border: '2px dashed #ddd', 
                  padding: '20px',
                  borderRadius: '8px',
                  backgroundColor: '#f9f9f9'
                }}>
                  <h4 style={{ margin: '0 0 15px 0', color: '#666', fontSize: '16px' }}>
                    {__('Content Area - Add any blocks below:', 'puroclean-tw')}
                  </h4>
                  <div {...innerBlocksProps} />
                </div>
              </div>
            </div>

            {/* Footer CTA Section - Card with padding */}
            <div style={{ padding: '24px 16px' }}>
              <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
                <div style={{
                  position: 'relative',
                  overflow: 'hidden',
                  minHeight: '120px',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {/* Background Image */}
                  {attributes.footerCtaImage?.url && (
                    <div style={{
                      position: 'absolute',
                      inset: '0',
                      backgroundImage: `url(${attributes.footerCtaImage.url})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }} />
                  )}
                  
                  {/* Color Overlay */}
                  <div style={{
                    position: 'absolute',
                    inset: '0',
                    background: attributes.colorScheme === 'red' ? 'var(--wp--preset--color--primary, #dc2626)' : 'var(--wp--preset--color--secondary, #2563eb)',
                    opacity: '0.8'
                  }} />
                  
                  {/* CTA Content */}
                  <div style={{
                    position: 'relative',
                    zIndex: '10',
                    padding: '32px 24px',
                    width: '100%'
                  }}>
                    <div style={{ textAlign: 'left' }}>
                      {attributes.footerCtaLink && attributes.footerCtaLink.trim() !== '' ? (
                        <div>
                          <h3 style={{ 
                            color: 'white', 
                            margin: (attributes.footerCtaSubtitle && attributes.footerCtaSubtitle.trim() !== '') ? '0 0 8px 0' : '0', 
                            fontSize: '20px',
                            fontWeight: 'bold',
                            lineHeight: '1.2',
                            cursor: 'pointer',
                            textDecoration: 'underline'
                          }}>
                            {attributes.footerCtaTitle || 'Find Your Local PuroClean'}
                          </h3>
                          {attributes.footerCtaSubtitle && attributes.footerCtaSubtitle.trim() !== '' && (
                            <p style={{ 
                              color: 'rgba(255, 255, 255, 0.9)', 
                              margin: '0',
                              fontSize: '16px',
                              lineHeight: '1.5'
                            }}>
                              {attributes.footerCtaSubtitle}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div>
                          <h3 style={{ 
                            color: 'white', 
                            margin: (attributes.footerCtaSubtitle && attributes.footerCtaSubtitle.trim() !== '') ? '0 0 8px 0' : '0', 
                            fontSize: '20px',
                            fontWeight: 'bold',
                            lineHeight: '1.2'
                          }}>
                            {attributes.footerCtaTitle || 'Find Your Local PuroClean'}
                          </h3>
                          {attributes.footerCtaSubtitle && attributes.footerCtaSubtitle.trim() !== '' && (
                            <p style={{ 
                              color: 'rgba(255, 255, 255, 0.9)', 
                              margin: '0',
                              fontSize: '16px',
                              lineHeight: '1.5'
                            }}>
                              {attributes.footerCtaSubtitle}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Triangle Element - Folded corner effect */}
                  <div style={{
                    position: 'absolute',
                    bottom: '0',
                    right: '0',
                    zIndex: '20'
                  }}>
                    <div style={{
                      width: '120px',
                      height: '120px',
                      backgroundColor: attributes.colorScheme === 'red' ? 'var(--wp--preset--color--secondary, #2563eb)' : 'var(--wp--preset--color--primary, #dc2626)',
                      clipPath: 'polygon(100% 0, 0 100%, 100% 100%)'
                    }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}